import { useLayoutEffect } from "react";

/**
 * Browser tab title management.
 *
 * Format convention is specific-name-first: `<page> - HappyDoc`. Narrow tabs
 * truncate the end, so the distinguishing text leads (matches Google Sheets,
 * Linear, Notion, GitHub). An empty/missing title falls back to the bare
 * product name so we never show a blank or stale tab.
 *
 * All format logic (separator, suffix, fallback) lives here so callers only
 * ever pass the page-specific part. Shared with TitleManager, which sets static
 * titles from route `handle` config.
 */
const PRODUCT = "HappyDoc";
const SEP = " - ";

export function formatTitle(title?: string | null): string {
    const trimmed = title?.trim();
    return trimmed ? `${trimmed}${SEP}${PRODUCT}` : PRODUCT;
}

/**
 * Imperatively set the document title for the current page. Used by pages whose
 * title is only known at render time (e.g. patient name, document-type name).
 * Static pages set their title declaratively via route `handle` (see
 * TitleManager) instead of calling this.
 *
 * useLayoutEffect (not useEffect) is deliberate: a passive effect can be deferred
 * by React 18 concurrent rendering and flush AFTER a subsequent navigation's
 * layout effects, leaving the previous page's title stranded in the tab (e.g.
 * "Visits" persisting on /settings). Layout effects run synchronously at commit
 * in tree order, so TitleManager (the first child of RootLayout) runs before the
 * page's hook, and an unmounting page never writes a late title.
 */
export function useDocumentTitle(title?: string | null): void {
    useLayoutEffect(() => {
        document.title = formatTitle(title);
    }, [title]);
}
